//  planned.js
//  Recurring commitments the user knows about before the first charge exists:
//  a new lease, a subscription that starts next month, a raise that lands in
//  October. Stored at users/{uid}/plannedSeries/{id}.
//
//  A plan is NOT a transaction. It never enters the ledger, budgets, variance
//  or the balance — each of those describes money that has actually moved, and
//  a fake instance would skew all of them (variance especially: the runway's
//  pessimistic line is built from it). Instead every projector takes plans as
//  a separate input:
//    - buildProjection → `planned` option (month-end forecast, scenarios)
//    - assessRunway    → plannedEvents() as extraEvents (runway, Today)
//
//  Once a real transaction matches, the plan is marked `linked` and the real
//  series takes over; linked plans drop out of every projector here.

import { expandRecurring } from './recurrence.js';
import { isMoneyIn } from '../txType.js';

export const PLANNED_KEY_PREFIX = 'planned:';

// Plans get their own key space so a plan named like an existing series can
// never merge into it — the two only become one series when a transaction links.
export const plannedKeyOf = (plan) => `${PLANNED_KEY_PREFIX}${plan.id}`;
export const isPlannedKey = (key) => typeof key === 'string' && key.startsWith(PLANNED_KEY_PREFIX);

// 'YYYY-MM-DD' parsed as LOCAL midnight (see runway.js parseDate for why).
export function parseDay(value) {
  if (!value) return null;
  const d = value instanceof Date
    ? new Date(value.getFullYear(), value.getMonth(), value.getDate())
    : (/^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value));
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(date, n) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
}

// Month arithmetic clamped to the target month's length, so Mar 31 steps back
// to Feb 28 rather than rolling over into Mar 3.
function addMonthsClamped(date, n) {
  const y = date.getFullYear();
  const m = date.getMonth() + n;
  const lastDay = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(date.getDate(), lastDay));
}

const STEP_DAYS = { daily: 1, weekly: 7, biweekly: 14 };
const STEP_MONTHS = { monthly: 1, semiannual: 6, yearly: 12 };

/**
 * One cycle before `date`. The projectors count occurrences strictly AFTER a
 * series' last instance, so anchoring a plan here is what makes its first
 * date count — anchoring on the first date itself silently drops it.
 */
export function previousOccurrence(date, recurrenceType) {
  const d = parseDay(date);
  if (!d) return null;
  if (STEP_MONTHS[recurrenceType]) return addMonthsClamped(d, -STEP_MONTHS[recurrenceType]);
  if (STEP_DAYS[recurrenceType]) return addDays(d, -STEP_DAYS[recurrenceType]);
  return addMonthsClamped(d, -1);
}

const round2 = (n) => parseFloat(n.toFixed(2));

/** Plans the projectors should still model: unlinked, dated, with an amount. */
export function activePlans(planned = []) {
  return (planned || []).filter((p) =>
    p && p.id && p.status !== 'linked' && Number(p.amount) > 0 && parseDay(p.firstDate) && p.recurrenceType);
}

/**
 * Entries shaped like projection.js's lastInstanceByRecurringSeries values,
 * keyed by plannedKeyOf. `keys` lets a scenario's scaleRecurring name a plan.
 */
export function plannedProjectionInstances(planned = []) {
  const out = {};
  activePlans(planned).forEach((p) => {
    const key = plannedKeyOf(p);
    out[key] = {
      date: previousOccurrence(p.firstDate, p.recurrenceType),
      recurrenceType: p.recurrenceType,
      type: p.type,
      amount: Number(p.amount),
      keys: new Set([key]),
    };
  });
  return out;
}

/**
 * Dated runway events for each active plan over (today, today + horizonDays].
 *
 * A plan whose first date has passed without a match is still owed: the one
 * charge we're waiting on is emitted (the runway lands anything dated today or
 * earlier on tomorrow) and tagged `overdue`. Earlier missed cycles aren't
 * stacked on top — a month-old plan shouldn't read as three bills due at once.
 *
 * `excludeAccountIds` drops plans paid from those accounts: a charge on a
 * credit card doesn't move cash on its date, so it has no place on the cash
 * runway.
 */
export function plannedEvents(planned = [], { today = new Date(), horizonDays = 60, excludeAccountIds = null } = {}) {
  const start = parseDay(today);
  const last = addDays(start, horizonDays);
  const out = [];
  activePlans(planned).forEach((p) => {
    if (excludeAccountIds && p.accountId && excludeAccountIds.has(p.accountId)) return;
    const first = parseDay(p.firstDate);
    const dates = [first, ...expandRecurring({ lastDate: first, recurrenceType: p.recurrenceType }, first, last)];
    const amount = round2(Number(p.amount));
    let overdueTaken = false;
    dates.forEach((d) => {
      if (d > last) return;
      const overdue = d <= start;
      if (overdue) {
        if (overdueTaken) return;
        overdueTaken = true;
      }
      out.push({
        date: d,
        seriesKey: plannedKeyOf(p),
        planId: p.id,
        description: p.name,
        type: p.type,
        amount,
        delta: isMoneyIn(p.type) ? amount : -amount,
        planned: true,
        ...(overdue ? { overdue: true } : {}),
      });
    });
  });
  return out;
}

/**
 * The plans a set of scenarios leaves in place. removeRecurring deltas can name
 * a plan by its planned key; everything else about plans passes through.
 */
export function plansAfterScenarios(planned = [], scenarios = []) {
  const removed = new Set();
  (scenarios || [])
    .filter((s) => s && s.enabled && Array.isArray(s.deltas))
    .forEach((s) => s.deltas.forEach((d) => {
      if (d && d.kind === 'removeRecurring' && isPlannedKey(d.seriesKey)) removed.add(d.seriesKey);
    }));
  if (removed.size === 0) return planned;
  return planned.filter((p) => !removed.has(plannedKeyOf(p)));
}

/** The date a plan is next expected, and whether that date has already passed. */
export function plannedNext(plan, today = new Date()) {
  const start = parseDay(today);
  const first = parseDay(plan.firstDate);
  if (!first) return { next: null, overdue: false };
  return { next: first, overdue: first < start };
}
